export const MULTI_FIXTURE_MIN = 5;

export const HOUSE_DISCOVERY_FILTERS = [
  { id: "all", label: "All", hint: "Every published house" },
  { id: "hot", label: "Hot", hint: "Has bettor activity" },
  { id: "multi", label: "Multi-fixture", hint: "Five or more fixtures" },
  { id: "fresh", label: "Fresh", hint: "Created recently" },
];

export const HOUSE_SORT_OPTIONS = [
  { id: "bettors", label: "Most active" },
  { id: "pool", label: "Largest pool" },
  { id: "fixtures", label: "Most fixtures" },
  { id: "name", label: "A → Z" },
];

export const getHouseBettorsTotal = (house) =>
  (house?.fixtures || []).reduce((sum, f) => sum + (Number(f.bettorsCount) || 0), 0);

export const getHousePoolTotal = (house) =>
  Number(house?.balance) ||
  (Number(house?.amountAvailable) || 0) + (Number(house?.pendingBalance) || 0);

export const isFreshHouse = (house) => {
  if (!house?.createdAt) return false;
  const createdAt = new Date(house.createdAt).getTime();
  if (Number.isNaN(createdAt)) return false;
  const sevenDays = 7 * 24 * 60 * 60 * 1000;
  return Date.now() - createdAt <= sevenDays;
};

const hasMultiFixtures = (house) =>
  (house?.fixtures?.length || 0) >= MULTI_FIXTURE_MIN;

export const getHouseDiscoveryCounts = (houses) => ({
  all: houses.length,
  hot: houses.filter((house) => getHouseBettorsTotal(house) > 0).length,
  multi: houses.filter(hasMultiFixtures).length,
  fresh: houses.filter(isFreshHouse).length,
});

export const getHousePlatformStats = (houses) => {
  let totalBettors = 0;
  let totalPool = 0;
  let fixtureCount = 0;
  let hotCount = 0;

  houses.forEach((house) => {
    totalBettors += getHouseBettorsTotal(house);
    totalPool += getHousePoolTotal(house);
    fixtureCount += house?.fixtures?.length || 0;
    if (getHouseBettorsTotal(house) > 0) hotCount += 1;
  });

  return {
    totalHouses: houses.length,
    hotCount,
    totalBettors,
    totalPool,
    fixtureCount,
  };
};

export const filterHousesByDiscovery = (houses, filterId) => {
  switch (filterId) {
    case "hot":
      return houses.filter((house) => getHouseBettorsTotal(house) > 0);
    case "multi":
      return houses.filter(hasMultiFixtures);
    case "fresh":
      return houses.filter(isFreshHouse);
    case "all":
    default:
      return houses;
  }
};

export const searchHouses = (houses, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return houses;

  return houses.filter((house) => (house.name || "").toLowerCase().includes(q));
};

export const sortHouses = (houses, sortBy) => {
  const list = [...houses];

  switch (sortBy) {
    case "pool":
      return list.sort((a, b) => getHousePoolTotal(b) - getHousePoolTotal(a));
    case "fixtures":
      return list.sort(
        (a, b) => (b.fixtures?.length || 0) - (a.fixtures?.length || 0)
      );
    case "name":
      return list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    case "bettors":
    default:
      return list.sort((a, b) => getHouseBettorsTotal(b) - getHouseBettorsTotal(a));
  }
};

export const fmtCompact = (n) =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(n) || 0);

/* ─────────────────────────────────────────────────────────────
   Atrium helpers — picking spotlight + lane segmentation.
   ───────────────────────────────────────────────────────────── */

const LIVE_FIXTURE_STATUSES = new Set(["1H", "HT", "2H", "ET", "BT", "P", "LIVE", "INT"]);
const SOON_WINDOW_MS = 24 * 60 * 60 * 1000;

export const isFixtureLive = (fixture) => {
  const short = (fixture?.status?.short || "").toUpperCase();
  return LIVE_FIXTURE_STATUSES.has(short);
};

export const isFixtureSoon = (fixture) => {
  if (!fixture?.date) return false;
  const t = new Date(fixture.date).getTime();
  if (Number.isNaN(t)) return false;
  const delta = t - Date.now();
  return delta > 0 && delta <= SOON_WINDOW_MS;
};

export const houseHasLiveOrSoonFixture = (house) => {
  const fixtures = house?.fixtures || [];
  return fixtures.some((f) => isFixtureLive(f) || isFixtureSoon(f));
};

export const getHouseHeat = (house) => {
  const bettors = getHouseBettorsTotal(house);
  const fixtures = house?.fixtures?.length || 0;
  const pool = getHousePoolTotal(house) / 1000;
  const liveBoost = houseHasLiveOrSoonFixture(house) ? 25 : 0;
  return bettors * 8 + fixtures * 4 + Math.min(pool, 200) + liveBoost;
};

export const rankHousesByHeat = (houses) =>
  [...houses].sort((a, b) => getHouseHeat(b) - getHouseHeat(a));

export const getTopPerformingHouses = (houses, limit = 5) =>
  rankHousesByHeat(houses).slice(0, Math.max(0, limit));

const dedupeHouses = (houses) => {
  const seen = new Set();
  const out = [];
  houses.forEach((h) => {
    const id = h?._id;
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(h);
  });
  return out;
};

export const getFeaturedFixture = (house) => {
  const fixtures = house?.fixtures || [];
  if (!fixtures.length) return null;
  const ranked = [...fixtures].sort((a, b) => {
    const liveA = isFixtureLive(a) ? 2 : isFixtureSoon(a) ? 1 : 0;
    const liveB = isFixtureLive(b) ? 2 : isFixtureSoon(b) ? 1 : 0;
    if (liveA !== liveB) return liveB - liveA;
    const bettorsDiff =
      (Number(b.bettorsCount) || 0) - (Number(a.bettorsCount) || 0);
    if (bettorsDiff !== 0) return bettorsDiff;
    const dateA = a?.date ? new Date(a.date).getTime() : Infinity;
    const dateB = b?.date ? new Date(b.date).getTime() : Infinity;
    return dateA - dateB;
  });
  return ranked[0] || null;
};

export const buildAtriumLanes = (houses) => {
  const live = houses.filter(houseHasLiveOrSoonFixture);
  const multi = houses.filter(
    (h) => (h?.fixtures?.length || 0) >= MULTI_FIXTURE_MIN
  );
  const fresh = houses.filter(isFreshHouse);
  const hot = houses.filter((h) => getHouseBettorsTotal(h) > 0);
  const all = rankHousesByHeat(houses);
  return [
    {
      id: "live",
      label: "Live & kicking off soon",
      hint: "Next 24h or in-play",
      tone: "live",
      houses: dedupeHouses(rankHousesByHeat(live)),
    },
    {
      id: "hot",
      label: "Trending right now",
      hint: "Highest bettor activity",
      tone: "hot",
      houses: dedupeHouses(rankHousesByHeat(hot)),
    },
    {
      id: "multi",
      label: "Multi-fixture battlegrounds",
      hint: "Five or more fixtures",
      tone: "multi",
      houses: dedupeHouses(rankHousesByHeat(multi)),
    },
    {
      id: "fresh",
      label: "Fresh this week",
      hint: "Just opened on the floor",
      tone: "fresh",
      houses: dedupeHouses(rankHousesByHeat(fresh)),
    },
    {
      id: "all",
      label: "Every house on the floor",
      hint: "Ranked by heat",
      tone: "all",
      houses: all,
    },
  ];
};

export const synthesizeMarqueeTicks = (houses) => {
  const ticks = [];
  houses.forEach((house) => {
    const featured = getFeaturedFixture(house);
    if (featured) {
      const home = featured.teams?.home?.name || "TBD";
      const away = featured.teams?.away?.name || "TBD";
      const status = isFixtureLive(featured)
        ? "LIVE"
        : isFixtureSoon(featured)
        ? "Soon"
        : "Upcoming";
      ticks.push({
        id: `${house._id}-fx-${featured._id}`,
        tone: status === "LIVE" ? "live" : status === "Soon" ? "soon" : "info",
        label: status,
        text: `${home} vs ${away}`,
        meta: house.name || "House",
      });
    }
    const bettors = getHouseBettorsTotal(house);
    if (bettors > 0) {
      ticks.push({
        id: `${house._id}-bt`,
        tone: "hot",
        label: "Hot",
        text: `${bettors} bettor${bettors === 1 ? "" : "s"} on`,
        meta: house.name || "House",
      });
    }
    const pool = getHousePoolTotal(house);
    if (pool > 0) {
      ticks.push({
        id: `${house._id}-pool`,
        tone: "pool",
        label: "Pool",
        text: `${fmtCompact(pool)} liquidity in`,
        meta: house.name || "House",
      });
    }
    if (isFreshHouse(house)) {
      ticks.push({
        id: `${house._id}-fresh`,
        tone: "fresh",
        label: "New",
        text: "Just opened",
        meta: house.name || "House",
      });
    }
  });
  return ticks;
};

export const fmtOdds = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return "—";
  return v.toFixed(2);
};

export const computeOddsPercents = (odds) => {
  const o = odds?.oneXTwo?.fullTime || odds?.oneXTwo?.firstHalf;
  if (!o) return null;
  const h = Number(o["1"]) || 0;
  const d = Number(o.X) || 0;
  const a = Number(o["2"]) || 0;
  if (h <= 0 || d <= 0 || a <= 0) return null;
  const invH = 1 / h;
  const invD = 1 / d;
  const invA = 1 / a;
  const sum = invH + invD + invA;
  return {
    home: Math.round((invH / sum) * 100),
    draw: Math.round((invD / sum) * 100),
    away: Math.round((invA / sum) * 100),
    homeOdds: h,
    drawOdds: d,
    awayOdds: a,
  };
};

/** Count bettable markets — uses marketsOffered when set, else walks odds tree. */
export const countOddsMarkets = (odds) => {
  if (!odds || typeof odds !== "object") return 0;
  let n = 0;
  for (const sectionVal of Object.values(odds)) {
    if (!sectionVal || typeof sectionVal !== "object") continue;
    const firstVal = Object.values(sectionVal)[0];
    if (typeof firstVal === "number" || typeof firstVal === "string") {
      if (Number(firstVal) > 1) n += 1;
      continue;
    }
    for (const keys of Object.values(sectionVal)) {
      if (keys && typeof keys === "object") n += Object.keys(keys).length;
    }
  }
  return n;
};

export const getFixtureMarketCount = (fixture) => {
  const offered = Number(fixture?.marketsOffered);
  if (offered > 0) return offered;
  return countOddsMarkets(fixture?.odds);
};
